import { getStoredToken, clearAuthData, isTokenExpired } from './auth';
import { parseErrorResponse } from './error';

/**
 * Serviços de transcrição de áudios (ferramenta de uso comum)
 *
 * Endpoints esperados no backend:
 *  POST   /api/Transcricao/drive                          -> inicia um job a partir de uma pasta do Google Drive
 *  POST   /api/Transcricao/drive/lote                     -> vários links de uma vez (entram na fila)
 *  GET    /api/Transcricao/jobs/ativos                    -> jobs em andamento e na fila (polling)
 *  GET    /api/Transcricao/jobs                           -> lista os jobs do usuário
 *  GET    /api/Transcricao/jobs/{jobId}                   -> status/detalhe de um job
 *  POST   /api/Transcricao/jobs/{jobId}/cancelar          -> cancela um job em andamento
 *  GET    /api/Transcricao/jobs/{jobId}/arquivos/{id}/download -> download da transcrição individual
 *  GET    /api/Transcricao/jobs/{jobId}/relatorio         -> download do relatório consolidado
 *  GET    /api/Transcricao/jobs/{jobId}/exportar          -> download em lote (.zip) de todas as transcrições
 *  GET    /api/Transcricao/resumo                         -> indicadores consolidados (com filtros)
 *  GET    /api/Transcricao/historico                      -> histórico de pastas transcritas (com filtros)
 *  GET    /api/Transcricao/projetos                       -> projetos disponíveis para filtro
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://162.240.235.102:7006';

const jsonHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/**
 * Redireciona para login quando a sessão expira
 */
const handleSessionExpired = () => {
  clearAuthData();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const authHeaders = () => {
  const token = getStoredToken();
  if (!token) {
    handleSessionExpired();
    throw new Error('Usuário não autenticado');
  }
  if (isTokenExpired()) {
    handleSessionExpired();
    throw new Error('Sessão expirada');
  }
  return jsonHeaders(token);
};

/**
 * Erro de uma chamada à API, preservando o status HTTP
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const ensureOk = async (response: Response, fallback?: string) => {
  if (response.ok) return;
  if (response.status === 401) {
    handleSessionExpired();
    throw new ApiError('Sessão expirada', 401);
  }
  const { message } = await parseErrorResponse(response, fallback);
  throw new ApiError(message, response.status);
};

// ==================== INTERFACES ====================

/**
 * NaFila: aguardando vaga no servidor (no máximo MAX_JOBS_SIMULTANEOS pastas são processadas ao mesmo tempo).
 * Pendente: enviado ao serviço de transcrição, listando os arquivos da pasta.
 */
export type StatusJob = 'NaFila' | 'Pendente' | 'Processando' | 'Concluido' | 'Erro' | 'Cancelado';
export type StatusArquivo = 'Pendente' | 'Processando' | 'Concluido' | 'Erro';

export interface ArquivoTranscricao {
  id: string;
  nome: string;
  status: StatusArquivo;
  progresso?: number;
  duracaoSegundos?: number;
  tamanhoBytes?: number;
  transcricao?: string;
  confiancaIa?: number;
  erro?: string;
}

export interface JobTranscricao {
  id: string;
  folderUrl: string;
  folderName?: string;
  status: StatusJob;
  /** Posição na fila (1 = próximo) quando status = NaFila */
  posicaoFila?: number | null;
  progresso: number;
  totalArquivos: number;
  arquivosConcluidos: number;
  arquivosComErro: number;
  criadoEm: string;
  concluidoEm?: string;
  tempoRestanteSegundos?: number;
  velocidade?: string;
  confiancaMedia?: number;
  erro?: string;
  arquivos: ArquivoTranscricao[];
}

export interface JobsResponse {
  jobs: JobTranscricao[];
}

export interface LinkInvalido {
  folderUrl: string;
  message: string;
}

export interface IniciarLoteResponse {
  jobs: JobTranscricao[];
  invalidos: LinkInvalido[];
}

export interface FiltrosPainel {
  periodoDias: number;
  projeto: string;
  pastaId: string;
}

export interface ResumoTranscricoes {
  pastasTranscritas: number;
  audiosRecebidos: number;
  transcricoes: number;
  naoTranscritos: number;
  taxaPerda: number;
  ultimaTranscricao?: string;
}

export interface PastaTranscrita {
  id: string;
  dataHora: string;
  pastaId: string;
  projeto?: string;
  subprojeto?: string;
  recebidos: number;
  transcritos: number;
  naoTranscritos: number;
}

export interface HistoricoResponse {
  pastas: PastaTranscrita[];
}

export interface ProjetosResponse {
  projetos: string[];
}

export const PERIODOS_PAINEL = [
  { value: 7, label: 'Últimos 7 dias' },
  { value: 30, label: 'Últimos 30 dias' },
  { value: 90, label: 'Últimos 90 dias' },
  { value: 0, label: 'Todo o período' },
];

export const FILTROS_PAINEL_PADRAO: FiltrosPainel = {
  periodoDias: 7,
  projeto: '',
  pastaId: '',
};

export const EXTENSOES_AUDIO_ACEITAS = '.wav, .mp3, .webm';

/**
 * Conta de serviço do Google Drive com a qual as pastas devem ser compartilhadas
 */
export const CONTA_SERVICO_DRIVE = process.env.REACT_APP_DRIVE_SERVICE_ACCOUNT || '';

/**
 * Quantas pastas o servidor processa ao mesmo tempo (as demais ficam na fila).
 * Só para texto de interface; o limite real é do backend (Transcriber:MaxJobsSimultaneos).
 */
export const MAX_JOBS_SIMULTANEOS = Number(process.env.REACT_APP_TRANSCRICAO_MAX_SIMULTANEOS) || 5;

/** Máximo de links aceitos por envio (mesmo limite do backend) */
export const MAX_LINKS_POR_LOTE = 50;

/** Já enviado ao serviço de transcrição e ainda rodando */
export const isJobEmAndamento = (job?: JobTranscricao | null): boolean =>
  Boolean(job && (job.status === 'Pendente' || job.status === 'Processando'));

export const isJobNaFila = (job?: JobTranscricao | null): boolean =>
  Boolean(job && job.status === 'NaFila');

/** Em andamento ou na fila: aparece na lista de acompanhamento e mantém o polling ligado */
export const isJobAtivo = (job?: JobTranscricao | null): boolean =>
  isJobEmAndamento(job) || isJobNaFila(job);

export const isJobFinalizado = (job?: JobTranscricao | null): boolean =>
  Boolean(
    job && (job.status === 'Concluido' || job.status === 'Erro' || job.status === 'Cancelado')
  );

// ==================== HELPERS ====================

/**
 * Valida se a URL informada é de uma pasta do Google Drive
 */
export const isGoogleDriveFolderUrl = (url: string): boolean => {
  const valor = url.trim();
  if (!valor) return false;
  return /^https:\/\/drive\.google\.com\/drive\/(u\/\d+\/)?folders\/[A-Za-z0-9_-]+/.test(valor);
};

/**
 * Separa o texto colado pelo usuário em links (um por linha, vírgula ou espaço), sem repetidos
 */
export const extrairLinks = (texto: string): string[] => {
  const vistos = new Set<string>();
  return texto
    .split(/[\s,;]+/)
    .map((valor) => valor.trim())
    .filter((valor) => {
      if (!valor || vistos.has(valor)) return false;
      vistos.add(valor);
      return true;
    });
};

/**
 * Extrai o ID da pasta a partir da URL do Google Drive
 */
export const extrairIdPasta = (url: string): string | null => {
  const match = url.trim().match(/folders\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

/**
 * Rótulo do lote: nome da pasta quando o backend envia, senão o ID da pasta
 */
export const nomeLote = (job: Pick<JobTranscricao, 'folderName' | 'folderUrl'>): string => {
  if (job.folderName) return job.folderName;
  const id = job.folderUrl ? extrairIdPasta(job.folderUrl) : null;
  return id ? `Pasta ${id}` : 'Pasta do Google Drive';
};

const nomeArquivoLote = (job: JobTranscricao): string =>
  (job.folderName || (job.folderUrl && extrairIdPasta(job.folderUrl)) || 'transcricoes').replace(
    /[^\w-]+/g,
    '_'
  );

/**
 * Formata a duração em segundos para mm:ss ou hh:mm:ss
 */
export const formatarDuracao = (segundos?: number): string => {
  if (!segundos && segundos !== 0) return '-';
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segs = Math.floor(segundos % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return horas > 0 ? `${horas}:${pad(minutos)}:${pad(segs)}` : `${minutos}:${pad(segs)}`;
};

/**
 * Formata o tempo restante estimado em uma descrição curta
 */
export const formatarTempoRestante = (segundos?: number | null): string => {
  if (!segundos && segundos !== 0) return 'calculando...';
  if (segundos < 60) {
    const valor = Math.max(1, Math.round(segundos));
    return `~${valor} segundo${valor !== 1 ? 's' : ''}`;
  }
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `~${String(minutos).padStart(2, '0')} minutos`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (resto) return `~${horas}h ${String(resto).padStart(2, '0')}min`;
  return `~${horas} hora${horas !== 1 ? 's' : ''}`;
};

/**
 * Formata uma duração longa no formato 05h 42m
 */
export const formatarDuracaoLonga = (segundos?: number): string => {
  if (!segundos) return '00h 00m';
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  return `${String(horas).padStart(2, '0')}h ${String(minutos).padStart(2, '0')}m`;
};

/**
 * Formata um percentual no padrão brasileiro
 */
export const formatarPercentual = (valor?: number, casas = 1): string => {
  if (!valor && valor !== 0) return '-';
  return `${valor.toFixed(casas).replace('.', ',')}%`;
};

/**
 * Formata um número inteiro no padrão brasileiro (1.476)
 */
export const formatarNumero = (valor?: number): string => {
  if (!valor && valor !== 0) return '-';
  return new Intl.NumberFormat('pt-BR').format(valor);
};

/**
 * Formata data e hora no padrão dd/MM - HH:mm
 */
export const formatarDataHoraCurta = (data?: string): string => {
  if (!data) return '-';
  const valor = new Date(data);
  if (Number.isNaN(valor.getTime())) return '-';
  const dia = String(valor.getDate()).padStart(2, '0');
  const mes = String(valor.getMonth() + 1).padStart(2, '0');
  const hora = String(valor.getHours()).padStart(2, '0');
  const minuto = String(valor.getMinutes()).padStart(2, '0');
  return `${dia}/${mes} - ${hora}:${minuto}`;
};

/**
 * Monta a query string dos filtros do painel
 */
export const montarQueryFiltros = (filtros: FiltrosPainel): string => {
  const params = new URLSearchParams();
  if (filtros.periodoDias) params.set('periodoDias', String(filtros.periodoDias));
  if (filtros.projeto) params.set('projeto', filtros.projeto);
  if (filtros.pastaId.trim()) params.set('pastaId', filtros.pastaId.trim());
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * Formata o tamanho do arquivo em unidades legíveis
 */
export const formatarTamanho = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Dispara o download de um blob no navegador
 */
const salvarBlob = (blob: Blob, nomeArquivo: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ==================== TRANSCRICAO FUNCTIONS ====================

/**
 * Inicia a transcrição automática de uma pasta do Google Drive
 * POST /api/Transcricao/drive
 */
export const iniciarTranscricaoDrive = async (
  folderUrl: string
): Promise<JobTranscricao> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/drive`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ folderUrl }),
  });

  await ensureOk(
    response,
    'Não foi possível iniciar a transcrição. Verifique o link e o compartilhamento da pasta.'
  );

  return response.json();
};

/**
 * Envia vários links de uma vez. Os que couberem no limite começam na hora;
 * os demais ficam na fila. Links inválidos voltam em `invalidos`.
 * POST /api/Transcricao/drive/lote
 */
export const iniciarTranscricaoLote = async (
  folderUrls: string[]
): Promise<IniciarLoteResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/drive/lote`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ folderUrls }),
  });

  await ensureOk(
    response,
    'Não foi possível iniciar as transcrições. Verifique os links e o compartilhamento das pastas.'
  );

  const data = (await response.json().catch(() => ({}))) as Partial<IniciarLoteResponse>;
  return { jobs: data.jobs || [], invalidos: data.invalidos || [] };
};

/**
 * Jobs em andamento (com progresso atualizado) e na fila (com posição), em ordem de criação.
 * Um job que acabou de terminar aparece uma última vez já com o status final.
 * GET /api/Transcricao/jobs/ativos
 */
export const listarJobsAtivos = async (): Promise<JobTranscricao[]> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs/ativos`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível acompanhar as transcrições em andamento');

  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data)) return data as JobTranscricao[];
  return (data as JobsResponse).jobs || [];
};

/**
 * Busca o status atual de um job de transcrição
 * GET /api/Transcricao/jobs/{jobId}
 */
export const obterJobTranscricao = async (jobId: string): Promise<JobTranscricao> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs/${jobId}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível obter o status da transcrição');

  return response.json();
};

/**
 * Lista as transcrições do usuário, mais recentes primeiro
 * GET /api/Transcricao/jobs
 */
export const listarJobsTranscricao = async (limite?: number): Promise<JobTranscricao[]> => {
  const query = limite ? `?limite=${limite}` : '';
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs${query}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível carregar o histórico de transcrições');

  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data)) return data as JobTranscricao[];
  return (data as JobsResponse).jobs || [];
};

/**
 * Cancela um job de transcrição em andamento
 * POST /api/Transcricao/jobs/{jobId}/cancelar
 */
export const cancelarTranscricao = async (jobId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs/${jobId}/cancelar`, {
    method: 'POST',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível cancelar a transcrição');
};

/**
 * Faz o download da transcrição de um arquivo específico
 * GET /api/Transcricao/jobs/{jobId}/arquivos/{arquivoId}/download
 */
export const baixarTranscricao = async (
  jobId: string,
  arquivo: ArquivoTranscricao
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/Transcricao/jobs/${jobId}/arquivos/${arquivo.id}/download`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  await ensureOk(response, 'Não foi possível baixar a transcrição');

  const blob = await response.blob();
  const nomeBase = arquivo.nome.replace(/\.[^.]+$/, '');
  salvarBlob(blob, `${nomeBase}.txt`);
};

/**
 * Faz o download do relatório consolidado de um job
 * GET /api/Transcricao/jobs/{jobId}/relatorio
 */
export const baixarRelatorioConsolidado = async (job: JobTranscricao): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs/${job.id}/relatorio`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível gerar o relatório consolidado');

  const blob = await response.blob();
  salvarBlob(blob, `Relatorio_${nomeArquivoLote(job)}_${new Date().getTime()}.txt`);
};

/**
 * Faz o download de todas as transcrições do lote em um único arquivo
 * GET /api/Transcricao/jobs/{jobId}/exportar
 */
export const exportarLote = async (job: JobTranscricao): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/jobs/${job.id}/exportar`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível exportar as transcrições do lote');

  const blob = await response.blob();
  salvarBlob(blob, `Transcricoes_${nomeArquivoLote(job)}_${new Date().getTime()}.zip`);
};

/**
 * Indicadores consolidados das transcrições
 * GET /api/Transcricao/resumo
 */
export const obterResumoTranscricoes = async (
  filtros: FiltrosPainel
): Promise<ResumoTranscricoes> => {
  const response = await fetch(
    `${API_BASE_URL}/api/Transcricao/resumo${montarQueryFiltros(filtros)}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  await ensureOk(response, 'Não foi possível carregar os indicadores de transcrição');

  return response.json();
};

/**
 * Histórico de pastas transcritas
 * GET /api/Transcricao/historico
 */
export const listarHistoricoPastas = async (
  filtros: FiltrosPainel
): Promise<PastaTranscrita[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/Transcricao/historico${montarQueryFiltros(filtros)}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  await ensureOk(response, 'Não foi possível carregar o histórico de pastas');

  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data)) return data as PastaTranscrita[];
  return (data as HistoricoResponse).pastas || [];
};

/**
 * Projetos disponíveis para o filtro do painel
 * GET /api/Transcricao/projetos
 */
export const listarProjetos = async (): Promise<string[]> => {
  const response = await fetch(`${API_BASE_URL}/api/Transcricao/projetos`, {
    method: 'GET',
    headers: authHeaders(),
  });

  await ensureOk(response, 'Não foi possível carregar os projetos');

  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data)) return data as string[];
  return (data as ProjetosResponse).projetos || [];
};
